import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Car, CarFront, FileText, Phone, CheckCircle, Pen, AlertTriangle, Package, Wrench, User, ChevronRight, ChevronUp, ChevronDown, Settings2, Eye, EyeOff, X, Target, Bell, GripVertical, Megaphone, ExternalLink, Star, ThumbsDown, UserX, Palmtree, Thermometer } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { ArrivalsTodayWidget } from '@/components/agenda/booking/ArrivalsTodayWidget';
import { useTranslation } from 'react-i18next';

const DEFAULT_WIDGETS = [
  { key: 'saasComms', label: 'Vortekio', enabled: true, fixed: true },
  { key: 'stats', label: 'stats', enabled: true, fixed: true },
  { key: 'appointments', label: 'appointments', enabled: true },
  { key: 'activeWork', label: 'activeWork', enabled: true },
  { key: 'woSummary', label: 'woSummary', enabled: true },
  { key: 'vehiclesReady', label: 'vehiclesReady', enabled: true },
  { key: 'quotesToFollow', label: 'quotesToFollow', enabled: true },
  { key: 'signedWaiting', label: 'signedWaiting', enabled: true },
  { key: 'upcomingServices', label: 'upcomingServices', enabled: true },
  { key: 'alerts', label: 'alerts', enabled: true },
  { key: 'notifications', label: 'notifications', enabled: false },
  { key: 'objectives', label: 'objectives', enabled: false },
  { key: 'satisfaction', label: 'satisfaction', enabled: true },
  { key: 'presence', label: 'Presence du jour', enabled: true },
];

const ReceptionDashboard = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [widgets, setWidgets] = useState(() => [
    { key: 'saasComms', label: 'Vortekio', enabled: true, fixed: true },
    { key: 'stats', label: 'stats', enabled: true, fixed: true },
    { key: 'appointments', label: 'appointments', enabled: true },
    { key: 'activeWork', label: 'activeWork', enabled: true },
    { key: 'woSummary', label: 'woSummary', enabled: true },
    { key: 'vehiclesReady', label: 'vehiclesReady', enabled: true },
    { key: 'quotesToFollow', label: 'quotesToFollow', enabled: true },
    { key: 'signedWaiting', label: 'signedWaiting', enabled: true },
    { key: 'upcomingServices', label: 'upcomingServices', enabled: true },
    { key: 'alerts', label: 'alerts', enabled: true },
    { key: 'notifications', label: 'notifications', enabled: false },
    { key: 'objectives', label: 'objectives', enabled: false },
    { key: 'satisfaction', label: 'satisfaction', enabled: true },
    { key: 'presence', label: 'Presence du jour', enabled: true },
  ]);
  const [showConfig, setShowConfig] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [saasComms, setSaasComms] = useState([]);
  const [satisfaction, setSatisfaction] = useState(null);
  const [presence, setPresence] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [res, presRes] = await Promise.all([
          axios.get(`${API}/dashboard/reception`),
          axios.get(`${API}/presence/today`).catch(() => ({ data: null })),
        ]);
        setData(res.data);
        if (res.data.satisfaction) setSatisfaction(res.data.satisfaction);
        setPresence(presRes.data);
      } catch (err) { console.error(err); }
    };
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    axios.get(`${API}/dashboard/widget-config`).then(r => {
      if (r.data?.widgets?.length) {
        // Merge saved config with defaults to include new widgets
        const saved = r.data.widgets;
        const savedKeys = new Set(saved.map(w => w.key));
        const merged = [...saved, ...DEFAULT_WIDGETS.filter(w => !savedKeys.has(w.key))];
        setWidgets(merged);
      }
    }).catch(() => {});
  }, []);

  // Load optional widget data
  useEffect(() => {
    const notifsEnabled = widgets.find(w => w.key === 'notifications')?.enabled;
    const objEnabled = widgets.find(w => w.key === 'objectives')?.enabled;
    if (notifsEnabled) {
      axios.get(`${API}/dashboard/recent-notifications`).then(r => setNotifications(r.data || [])).catch(() => {});
    }
    if (objEnabled) {
      axios.get(`${API}/dashboard/current-objectives`).then(r => setObjectives(r.data || [])).catch(() => {});
    }
    // Always load SaaS communications
    axios.get(`${API}/dashboard/saas-communications`).then(r => setSaasComms(r.data || [])).catch(() => {});
  }, [widgets]);

  const saveWidgets = useCallback(async (newWidgets) => {
    setWidgets(newWidgets);
    try {
      await axios.put(`${API}/dashboard/widget-config`, { widgets: newWidgets });
    } catch {}
  }, []);

  const toggleWidget = (key) => {
    const updated = widgets.map(w => w.key === key && !w.fixed ? { ...w, enabled: !w.enabled } : w);
    saveWidgets(updated);
  };

  const moveWidget = (key, direction) => {
    const idx = widgets.findIndex(w => w.key === key);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= widgets.length) return;
    if (widgets[target].fixed || widgets[idx].fixed) return;
    const copy = [...widgets];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    saveWidgets(copy);
  };

  if (!data) return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">{t('common.loading')}</div>
      </div>
    </AdminLayout>
  );

  const s = data.stats || {};
  const appts = data.today_appointments || [];
  const ready = data.vehicles_ready || [];
  const signed = data.signed_waiting || [];
  const activeWork = data.active_work || [];
  const quotesToFollow = data.quotes_to_follow || [];
  const services = data.upcoming_services || [];
  const lowStock = data.low_stock_alerts || [];
  const atelierAlerts = data.open_stock_alerts || [];
  const workshop = data.workshop_availability || {};

  const STATUS_CONFIG = {
    scheduled: { label: 'Planifie', color: 'bg-blue-100 text-blue-700', icon: Clock },
    confirmed: { label: 'Confirme', color: 'bg-indigo-100 text-indigo-700', icon: CheckCircle },
    in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: Wrench },
    completed: { label: 'Termine', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    cancelled: { label: 'Annule', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  };

  const CATEGORY_COLORS = {
    reparation: 'bg-red-500', entretien: 'bg-emerald-500', controle: 'bg-blue-500',
    pneus: 'bg-amber-500', carrosserie: 'bg-purple-500', diagnostic: 'bg-indigo-500', autre: 'bg-slate-400',
  };

  const isEnabled = (key) => widgets.find(w => w.key === key)?.enabled;
  const enabledWidgets = widgets.filter(w => w.enabled && w.key !== 'stats');

  // Widget renderers
  const renderWidget = (widget) => {
    switch (widget.key) {
      case 'appointments': return <AppointmentsWidget key="appointments" appts={appts} s={s} navigate={navigate} STATUS_CONFIG={STATUS_CONFIG} CATEGORY_COLORS={CATEGORY_COLORS} />;
      case 'activeWork': return <ActiveWorkWidget key="activeWork" activeWork={activeWork} navigate={navigate} />;
      case 'woSummary': return <WoSummaryWidget key="woSummary" s={s} navigate={navigate} />;
      case 'vehiclesReady': return ready.length > 0 ? <VehiclesReadyWidget key="vehiclesReady" ready={ready} navigate={navigate} /> : null;
      case 'quotesToFollow': return quotesToFollow.length > 0 ? <QuotesWidget key="quotesToFollow" quotesToFollow={quotesToFollow} navigate={navigate} /> : null;
      case 'signedWaiting': return signed.length > 0 ? <SignedWaitingWidget key="signedWaiting" signed={signed} navigate={navigate} /> : null;
      case 'upcomingServices': return services.length > 0 ? <ServicesWidget key="upcomingServices" services={services} navigate={navigate} /> : null;
      case 'alerts': return (lowStock.length > 0 || atelierAlerts.length > 0) ? <AlertsWidget key="alerts" lowStock={lowStock} atelierAlerts={atelierAlerts} /> : null;
      case 'notifications': return <NotificationsWidget key="notifications" notifications={notifications} navigate={navigate} />;
      case 'objectives': return <ObjectivesWidget key="objectives" objectives={objectives} navigate={navigate} />;
      case 'saasComms': return saasComms.length > 0 ? <SaasCommsWidget key="saasComms" comms={saasComms} /> : null;
      case 'satisfaction': return satisfaction ? <SatisfactionWidget key="satisfaction" data={satisfaction} navigate={navigate} /> : null;
      case 'presence': return presence ? <PresenceWidget key="presence" data={presence} /> : null;
      default: return null;
    }
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="reception-dashboard">
        {/* Header with config button */}
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-slate-900 dark:text-white font-heading">{t('dashboard.title')}</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfig(true)}
            className="text-xs gap-1.5"
            data-testid="widget-config-btn"
          >
            <Settings2 className="w-3.5 h-3.5" />
            {t('dashboard.customize')}
          </Button>
        </div>

        {/* SaaS Communications Banner */}
        {isEnabled('saasComms') && saasComms.length > 0 && <SaasCommsWidget comms={saasComms} />}

        <ArrivalsTodayWidget />

        {/* Stats row - always visible */}
        {isEnabled('stats') && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reception-stats">
            <StatCard icon={Calendar} label={t('dashboard.today_appointments')} value={s.today_total} accent="blue" onClick={() => navigate('/admin/agenda')} />
            <StatCard icon={Wrench} label={t('dashboard.in_progress')} value={s.wo_in_progress} accent="amber" onClick={() => navigate('/admin/work-orders')} />
            <StatCard icon={CheckCircle} label={t('dashboard.vehicles_ready')} value={ready.length} accent="emerald" onClick={() => {}} />
            <StatCard icon={Users} label={t('clients.title')} value={s.total_clients} accent="slate" onClick={() => navigate('/admin/clients')} />
          </div>
        )}

        {/* Workshop Availability Card */}
        <WorkshopAvailabilityCard workshop={workshop} />

        {/* Dynamic widgets in a responsive grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {enabledWidgets.map(w => renderWidget(w))}
        </div>

        {/* Config Panel Overlay */}
        {showConfig && (
          <WidgetConfigPanel
            widgets={widgets}
            onToggle={toggleWidget}
            onMove={moveWidget}
            onClose={() => setShowConfig(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// ═══════════════════════ WIDGET CONFIG PANEL ═══════════════════════
const WidgetConfigPanel = ({ widgets, onToggle, onMove, onClose }) => {
  const { t } = useTranslation();
  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="widget-config-panel">
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h3 className="text-base font-bold text-slate-900 dark:text-white font-heading">{t('dashboard.customize_dashboard')}</h3>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="widget-config-close">
          <X className="w-5 h-5 text-slate-500" />
        </button>
      </div>
      <div className="p-4 space-y-1.5 max-h-[60vh] overflow-y-auto">
        {widgets.map((w, idx) => (
          <div
            key={w.key}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${w.enabled ? 'bg-slate-50 dark:bg-slate-700/50' : 'opacity-60'}`}
            data-testid={`widget-row-${w.key}`}
          >
            <GripVertical className="w-4 h-4 text-slate-300 shrink-0" />
            <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-200">{w.label}</span>
            {!w.fixed && (
              <div className="flex items-center gap-1">
                <button onClick={() => onMove(w.key, 'up')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600" data-testid={`widget-up-${w.key}`}>
                  <ChevronUp className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <button onClick={() => onMove(w.key, 'down')} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600" data-testid={`widget-down-${w.key}`}>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                </button>
                <button
                  onClick={() => onToggle(w.key)}
                  className={`p-1.5 rounded-lg transition-colors ${w.enabled ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-slate-200 text-slate-400 dark:bg-slate-600 dark:text-slate-400'}`}
                  data-testid={`widget-toggle-${w.key}`}
                >
                  {w.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
              </div>
            )}
            {w.fixed && <Badge className="text-[10px] bg-slate-200 text-slate-500 dark:bg-slate-600 dark:text-slate-300">{t('dashboard.fixed')}</Badge>}
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700">
        <Button onClick={onClose} className="w-full" data-testid="widget-config-done">{t('common.finish')}</Button>
      </div>
    </div>
  </div>
  );
};

// ═══════════════════════ WIDGET COMPONENTS ═══════════════════════

const Section = ({ title, badge, badgeColor, action, actionLabel, children }) => {
  const { t } = useTranslation();
  return (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2 font-heading">
        {title}
        {badge && <Badge className={`${badgeColor || 'bg-slate-100 text-slate-700'} text-xs`}>{badge}</Badge>}
      </h2>
      {action && <Button variant="ghost" size="sm" onClick={action} className="text-xs text-slate-500">{actionLabel || t('common.view')}</Button>}
    </div>
    {children}
  </div>
  );
};

const AppointmentsWidget = ({ appts, s, navigate, STATUS_CONFIG, CATEGORY_COLORS }) => {
  const { t } = useTranslation();

  const WO_STATUS_LABEL = {
    open: 'Ouverte', signed: 'Signée', in_progress: 'En cours', completed: 'Terminée', invoiced: 'Facturée',
  };
  const WO_STATUS_COLOR = {
    open: 'bg-blue-100 text-blue-700', signed: 'bg-indigo-100 text-indigo-700',
    in_progress: 'bg-amber-100 text-amber-700', completed: 'bg-emerald-100 text-emerald-700',
    invoiced: 'bg-slate-100 text-slate-600',
  };

  return (
  <div className="lg:col-span-2">
    <Section title={t('dashboard.today_rdv')} badge={`${s.today_total}`} badgeColor="bg-blue-100 text-blue-700" action={() => navigate('/admin/agenda')} actionLabel={t('agenda.title')}>
      {appts.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-2">{t('dashboard.no_appointments')}</p>
      ) : (
        <Card className="rounded-xl overflow-hidden" data-testid="today-appointments-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Heure</th>
                  <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Client</th>
                  <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Vehicule</th>
                  <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Statut</th>
                  <th className="text-left py-2 px-3 text-xs uppercase text-slate-500 font-medium">Mecanicien</th>
                  <th className="text-center py-2 px-3 text-xs uppercase text-slate-500 font-medium">H. allouees</th>
                  <th className="text-center py-2 px-3 text-xs uppercase text-slate-500 font-medium">H. restantes</th>
                </tr>
              </thead>
              <tbody>
                {appts.map(a => {
                  const st = STATUS_CONFIG[a.status] || STATUS_CONFIG.scheduled;
                  const StIcon = st.icon;
                  const woStatus = a.wo_status;
                  return (
                    <tr key={a.id}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => a.work_order_id ? navigate(`/admin/work-orders/${a.work_order_id}`) : navigate('/admin/agenda')}
                      data-testid={`today-appt-${a.id}`}>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-1 h-6 rounded-full ${CATEGORY_COLORS[a.category] || 'bg-slate-300'}`} />
                          <span className="font-bold font-mono text-slate-900">{a.time}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <p className="font-medium text-slate-900 truncate max-w-[160px]">{a.client_name}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <p className="text-slate-600 truncate max-w-[160px]">{a.vehicle_info}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        {woStatus ? (
                          <Badge className={`text-[10px] ${WO_STATUS_COLOR[woStatus] || 'bg-slate-100 text-slate-600'}`}>
                            {WO_STATUS_LABEL[woStatus] || woStatus}
                          </Badge>
                        ) : (
                          <Badge className={`text-[10px] ${st.color}`}><StIcon className="w-3 h-3 mr-0.5" />{st.label}</Badge>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`text-xs ${a.mechanic ? 'font-medium text-indigo-600' : 'text-slate-400 italic'}`}>
                          {a.mechanic || '—'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {a.allocated_hours > 0 ? (
                          <span className="font-mono font-bold text-slate-700">{a.allocated_hours}h</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {a.allocated_hours > 0 ? (
                          <span className={`font-mono font-bold ${a.remaining_hours <= 0 ? 'text-red-600' : a.remaining_hours < 1 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {a.remaining_hours}h
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </Section>
  </div>
);
};

const ActiveWorkWidget = ({ activeWork, navigate }) => {
  const { t } = useTranslation();
  return (
  <div className="lg:col-span-2">
    <Section title={t('dashboard.active_work_orders')} badge={`${activeWork.length}`} badgeColor="bg-amber-100 text-amber-700" action={() => navigate('/admin/work-orders')} actionLabel={t('work_orders.title')}>
      {activeWork.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-2">{t('dashboard.no_active_wo')}</p>
      ) : (
        <div className="space-y-2" data-testid="active-work-list">
          {activeWork.map(w => {
            const pct = w.allocated_hours > 0 ? Math.min((w.actual_hours / w.allocated_hours) * 100, 100) : 0;
            const overTime = w.allocated_hours > 0 && w.actual_hours > w.allocated_hours;
            return (
              <Card key={w.wo_id} className="p-3 rounded-xl hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/admin/work-orders/${w.wo_id}`)} data-testid={`active-wo-${w.wo_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-1 h-10 rounded-full ${overTime ? 'bg-red-500' : 'bg-amber-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{w.client_name}</span>
                      {w.mechanic && <Badge className="text-[10px] bg-indigo-100 text-indigo-700"><User className="w-3 h-3 mr-0.5" />{w.mechanic}</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      <span className="flex items-center gap-1"><Car className="w-3 h-3" />{w.vehicle_info}</span>
                      <span className={`font-bold ${overTime ? 'text-red-600' : 'text-emerald-600'}`}>{w.actual_hours}h{w.allocated_hours > 0 ? ` / ${w.allocated_hours}h` : ''}</span>
                    </div>
                  </div>
                  {w.allocated_hours > 0 && (
                    <div className="w-16 flex flex-col items-end gap-1">
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${overTime ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} /></div>
                      <span className="text-[10px] text-slate-400">{Math.round(pct)}%</span>
                    </div>
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Section>
  </div>
  );
};

const WoSummaryWidget = ({ s, navigate }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('work_orders.title')}>
      <Card className="p-4 rounded-xl space-y-3" data-testid="wo-summary">
        <WoBar label={t('dashboard.open')} count={s.wo_open} color="bg-blue-500" total={s.wo_open + s.wo_signed + s.wo_in_progress + s.wo_completed} onClick={() => navigate('/admin/work-orders')} />
        <WoBar label={t('dashboard.signed')} count={s.wo_signed} color="bg-indigo-500" total={s.wo_open + s.wo_signed + s.wo_in_progress + s.wo_completed} onClick={() => navigate('/admin/work-orders')} />
        <WoBar label={t('dashboard.in_progress')} count={s.wo_in_progress} color="bg-amber-500" total={s.wo_open + s.wo_signed + s.wo_in_progress + s.wo_completed} onClick={() => navigate('/admin/work-orders')} />
        <WoBar label={t('dashboard.done')} count={s.wo_completed} color="bg-emerald-500" total={s.wo_open + s.wo_signed + s.wo_in_progress + s.wo_completed} onClick={() => navigate('/admin/work-orders')} />
      </Card>
    </Section>
  </div>
  );
};

const VehiclesReadyWidget = ({ ready, navigate }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('dashboard.vehicles_ready')} badge={`${ready.length}`} badgeColor="bg-emerald-100 text-emerald-700">
      <div className="space-y-2" data-testid="vehicles-ready-list">
        {ready.slice(0, 5).map(v => (
          <Card key={v.wo_id} className="p-3 rounded-xl border border-emerald-200 dark:border-emerald-800 cursor-pointer hover:shadow-sm transition-shadow" onClick={() => navigate(`/admin/work-orders/${v.wo_id}`)}>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{v.client_name}</p>
                <p className="text-xs text-slate-500">{v.vehicle_info}</p>
              </div>
              {v.client_phone && (
                <a href={`tel:${v.client_phone}`} className="text-slate-400 hover:text-slate-600" onClick={e => e.stopPropagation()}><Phone className="w-3.5 h-3.5" /></a>
              )}
            </div>
          </Card>
        ))}
      </div>
    </Section>
  </div>
  );
};

const QuotesWidget = ({ quotesToFollow, navigate }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('reminders.title')} badge={`${quotesToFollow.length}`} badgeColor="bg-orange-100 text-orange-700">
      <div className="space-y-2" data-testid="quotes-to-follow">
        {quotesToFollow.map(q => (
          <Card key={q.id} className="p-3 rounded-xl border border-orange-200 dark:border-orange-800 cursor-pointer hover:shadow-sm transition-shadow" onClick={() => q.wo_id ? navigate(`/admin/work-orders/${q.wo_id}`) : navigate('/admin/billing')}>
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{q.client_name}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                  {q.reference && <span className="font-mono">{q.reference}</span>}
                  <span className="font-bold text-orange-600">{q.total_ttc?.toFixed(2)} CHF</span>
                </div>
              </div>
              <div className="text-right">
                <Badge className="bg-orange-100 text-orange-700 text-[10px]">{q.days_ago}j</Badge>
                {q.client_phone && (
                  <a href={`tel:${q.client_phone}`} className="block mt-1 text-slate-400 hover:text-slate-600" onClick={e => e.stopPropagation()}><Phone className="w-3 h-3 ml-auto" /></a>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  </div>
  );
};

const SignedWaitingWidget = ({ signed, navigate }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('dashboard.signed')} badge={`${signed.length}`} badgeColor="bg-blue-100 text-blue-700">
      <div className="space-y-2" data-testid="signed-waiting-list">
        {signed.map(w => (
          <Card key={w.wo_id} className="p-3 rounded-xl border border-blue-200 dark:border-blue-800 cursor-pointer hover:shadow-sm" onClick={() => navigate(`/admin/work-orders/${w.wo_id}`)}>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{w.client_name}</p>
            <p className="text-xs text-slate-500">{w.vehicle_info}</p>
          </Card>
        ))}
      </div>
    </Section>
  </div>
  );
};

const ServicesWidget = ({ services, navigate }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('reminders.service_reminders')} badge={`${services.length}`} badgeColor="bg-violet-100 text-violet-700">
      <div className="space-y-2" data-testid="upcoming-services-list">
        {services.map(v => (
          <Card key={v.id} className="p-3 rounded-xl hover:shadow-sm cursor-pointer" onClick={() => navigate('/admin/vehicles')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-200">{v.client_name}</p>
                <p className="text-xs text-slate-500">{v.brand} {v.model} - {v.plate}</p>
              </div>
              <span className="text-sm font-bold text-violet-600">{v.next_service_date}</span>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  </div>
  );
};

const AlertsWidget = ({ lowStock, atelierAlerts }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('inventory.stock_alert')} badge={`${lowStock.length + atelierAlerts.length}`} badgeColor="bg-red-100 text-red-700">
      <div className="space-y-2" data-testid="alerts-section">
        {lowStock.map((p, i) => (
          <Card key={`stock-${i}`} className="p-3 rounded-xl border-l-4 border-l-red-400">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-900 dark:text-slate-200">{p.name}</span>
              <Badge className="bg-red-100 text-red-700 text-xs">{p.quantity} {t('inventory.remaining')}</Badge>
            </div>
          </Card>
        ))}
        {atelierAlerts.map(a => (
          <Card key={a.id} className="p-3 rounded-xl border-l-4 border-l-amber-400">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-200">{a.item_name}</span>
            {a.description && <p className="text-xs text-slate-500 mt-0.5 italic">"{a.description}"</p>}
          </Card>
        ))}
      </div>
    </Section>
  </div>
  );
};

const NotificationsWidget = ({ notifications, navigate }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('nav.notifications')} badge={`${notifications.length}`} badgeColor="bg-blue-100 text-blue-700" action={() => navigate('/admin/notifications')} actionLabel={t('common.view')}>
      {notifications.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-2">{t('common.no_data')}</p>
      ) : (
        <div className="space-y-2" data-testid="notifications-widget">
          {notifications.slice(0, 5).map((n, i) => (
            <Card key={n.id || i} className="p-3 rounded-xl">
              <div className="flex items-start gap-2">
                <Bell className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{n.title}</p>
                  <p className="text-xs text-slate-500 truncate">{n.message}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Section>
  </div>
);
};

const ObjectivesWidget = ({ objectives, navigate }) => {
  const { t } = useTranslation();
  return (
  <div>
    <Section title={t('dashboard.monthly_objectives')} action={() => navigate('/admin/objectives')} actionLabel={t('dashboard.manage')}>
      {objectives.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-2">{t('dashboard.no_objectives')}</p>
      ) : (
        <div className="space-y-2" data-testid="objectives-widget">
          {objectives.slice(0, 4).map((o, i) => {
            const pct = o.target > 0 ? Math.min((o.current / o.target) * 100, 100) : 0;
            return (
              <Card key={o.id || i} className="p-3 rounded-xl">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-200">{o.label || o.metric}</span>
                  <span className="text-xs font-bold text-slate-600">{Math.round(pct)}%</span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Section>
  </div>
  );
};

// ═══════════════════════ PRESENCE DU JOUR WIDGET ═══════════════════════

const PresenceWidget = ({ data }) => {
  const c = data.counts || {};
  const categories = [
    { key: 'present', label: 'Presents', items: data.present, icon: CheckCircle, color: 'emerald', count: c.present },
    { key: 'on_vacation', label: 'Vacances', items: data.on_vacation, icon: Palmtree, color: 'blue', count: c.on_vacation },
    { key: 'sick', label: 'Maladie', items: data.sick, icon: Thermometer, color: 'orange', count: c.sick },
    { key: 'other_leave', label: 'Autre conge', items: data.other_leave, icon: Calendar, color: 'purple', count: c.other_leave },
    { key: 'absent', label: 'Non pointes', items: data.absent, icon: UserX, color: 'red', count: c.absent },
  ].filter(cat => cat.count > 0);

  const colorMap = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', icon: 'text-emerald-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', icon: 'text-blue-500' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', icon: 'text-orange-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500', icon: 'text-purple-500' },
    red: { bg: 'bg-red-50', text: 'text-red-600', badge: 'bg-red-100 text-red-700', dot: 'bg-red-400', icon: 'text-red-400' },
  };

  return (
    <div className="lg:col-span-2" data-testid="presence-widget">
      <Section title="Presence du jour" badge={`${data.total_employees}`} badgeColor="bg-sky-100 text-sky-700">
        {/* Counter pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          {categories.map(cat => {
            const cm = colorMap[cat.color];
            const Icon = cat.icon;
            return (
              <div key={cat.key} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ${cm.badge} text-xs font-semibold`} data-testid={`presence-pill-${cat.key}`}>
                <Icon className="w-3.5 h-3.5" />
                <span>{cat.count} {cat.label}</span>
              </div>
            );
          })}
        </div>

        {/* Employee grid by status */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {categories.map(cat => {
            const cm = colorMap[cat.color];
            const Icon = cat.icon;
            return cat.items.map(emp => (
              <div key={emp.id} className={`flex items-center gap-2 p-2 ${cm.bg} rounded-lg`} data-testid={`presence-${cat.key}-${emp.id}`}>
                <Icon className={`w-3.5 h-3.5 ${cm.icon} flex-shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{emp.name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{emp.position || emp.role || ''}</p>
                </div>
              </div>
            ));
          })}
        </div>
      </Section>
    </div>
  );
};

// ═══════════════════════ SHARED COMPONENTS ═══════════════════════

const WorkshopAvailabilityCard = ({ workshop }) => {
  if (!workshop || !workshop.capacity_minutes) return null;
  const pct = workshop.usage_percent || 0;
  const remaining = workshop.remaining_hours || 0;
  const allocated = workshop.allocated_hours || 0;
  const capacity = workshop.capacity_hours || 8.2;
  const activeCount = workshop.active_wo_count || 0;

  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  const statusColor = pct >= 90 ? 'text-red-600' : pct >= 70 ? 'text-amber-600' : 'text-emerald-600';
  const statusLabel = pct >= 100 ? 'Surcharge' : pct >= 90 ? 'Quasi complet' : pct >= 70 ? 'Charge' : pct >= 40 ? 'Disponible' : 'Libre';
  const statusBg = pct >= 90 ? 'bg-red-50' : pct >= 70 ? 'bg-amber-50' : 'bg-emerald-50';

  const formatH = (h) => {
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    return mins > 0 ? `${hrs}h${mins.toString().padStart(2, '0')}` : `${hrs}h`;
  };

  return (
    <Card className="p-4 rounded-xl" data-testid="workshop-availability-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Wrench className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900">Disponibilite Atelier</h3>
            <p className="text-[10px] text-slate-400">1 mecanicien &middot; 7h30-12h / 13h30-17h30</p>
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusBg} ${statusColor}`} data-testid="workshop-status">
          {statusLabel}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mb-3" data-testid="workshop-progress-bar">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-lg font-bold text-slate-900" data-testid="workshop-capacity">{formatH(capacity)}</p>
          <p className="text-[10px] text-slate-400 font-medium">Capacite/jour</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-lg font-bold text-amber-600" data-testid="workshop-allocated">{formatH(allocated)}</p>
          <p className="text-[10px] text-slate-400 font-medium">Alloue ({activeCount} fiches)</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <p className={`text-lg font-bold ${statusColor}`} data-testid="workshop-remaining">{formatH(remaining)}</p>
          <p className="text-[10px] text-slate-400 font-medium">Disponible</p>
        </div>
      </div>
    </Card>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = 'slate', onClick }) => {
  const colors = { blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400', amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400', emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400', slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' };
  return (
    <Card className={`p-3 rounded-xl ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={onClick} data-testid={`stat-${label.replace(/[^a-z]/gi, '-').toLowerCase()}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[accent]} mb-1`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
      <p className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</p>
    </Card>
  );
};

const WoBar = ({ label, count, color, total, onClick }) => {
  const { t } = useTranslation();
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className={`space-y-1 ${onClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 -mx-2 px-2 py-1 rounded-lg' : ''}`} onClick={onClick}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600 dark:text-slate-400 font-medium">{label}</span>
        <span className="font-bold text-slate-900 dark:text-white">{count}</span>
      </div>
      <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ═══════════════ SATISFACTION WIDGET ═══════════════
const StarRating = ({ rating, size = 'w-4 h-4' }) => {
  const { t } = useTranslation();
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={`${size} ${i <= full ? 'fill-amber-400 text-amber-400' : half && i === full + 1 ? 'fill-amber-400/50 text-amber-400' : 'text-slate-200 dark:text-slate-600'}`} />
      ))}
    </div>
  );
};

const SatisfactionWidget = ({ data, navigate }) => {
  const { t } = useTranslation();
  const ratingColor = data.avg_rating_all >= 4 ? 'text-emerald-600' : data.avg_rating_all >= 3 ? 'text-amber-600' : 'text-red-600';
  const unhappy = data.unhappy_clients || [];

  return (
    <div className="lg:col-span-2" data-testid="satisfaction-widget">
      <Section title={t('dashboard.satisfaction_score')} badge={`${data.total_reviews} ${t('satisfaction.reviews')}`} badgeColor="bg-amber-100 text-amber-700" action={() => navigate('/admin/satisfaction')} actionLabel={t('common.details')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="p-4 rounded-xl text-center" data-testid="satisfaction-avg-card">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{t('dashboard.global_score')}</p>
            <p className={`text-3xl font-black ${ratingColor}`} data-testid="satisfaction-avg-value">{data.avg_rating_all || '-'}</p>
            <StarRating rating={data.avg_rating_all} size="w-5 h-5" />
            <p className="text-[11px] text-slate-400 mt-1">{data.total_reviews} {t('satisfaction.reviews_total')}</p>
          </Card>

          <Card className="p-4 rounded-xl text-center" data-testid="satisfaction-month-card">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{t('dashboard.this_month')}</p>
            <p className={`text-3xl font-black ${data.avg_rating_month >= 4 ? 'text-emerald-600' : data.avg_rating_month >= 3 ? 'text-amber-600' : data.avg_rating_month > 0 ? 'text-red-600' : 'text-slate-300'}`} data-testid="satisfaction-month-value">
              {data.avg_rating_month || '-'}
            </p>
            <StarRating rating={data.avg_rating_month} size="w-5 h-5" />
            <p className="text-[11px] text-slate-400 mt-1">{data.month_reviews} {t('satisfaction.reviews_this_month')}</p>
          </Card>

          <Card className={`p-4 rounded-xl ${unhappy.length > 0 ? 'border border-red-200 dark:border-red-800' : ''}`} data-testid="satisfaction-unhappy-card">
            <div className="flex items-center gap-2 mb-2">
              <ThumbsDown className={`w-4 h-4 ${unhappy.length > 0 ? 'text-red-500' : 'text-slate-300'}`} />
              <p className="text-xs text-slate-500 uppercase tracking-wide">{t('dashboard.to_callback')}</p>
              {unhappy.length > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]">{unhappy.length}</Badge>}
            </div>
            {unhappy.length === 0 ? (
              <p className="text-sm text-slate-400 italic">{t('dashboard.no_unhappy_clients')}</p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="unhappy-clients-list">
                {unhappy.map((c, i) => (
                  <div key={c.client_id || i} className="flex items-center justify-between gap-2 text-sm" data-testid={`unhappy-client-${i}`}>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-slate-800 dark:text-slate-200 truncate block">{c.client_name}</span>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <StarRating rating={c.rating} size="w-3 h-3" />
                        {c.vehicle && <span className="truncate">{c.vehicle}</span>}
                      </div>
                    </div>
                    {c.client_phone && (
                      <a href={`tel:${c.client_phone}`} className="shrink-0 p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400" data-testid={`call-unhappy-${i}`}>
                        <Phone className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </Section>
    </div>
  );
};

// ═══════════════ SAAS COMMUNICATIONS WIDGET ═══════════════
const COMM_TYPE_STYLES = {
  info: { bg: 'from-blue-500 to-blue-600', icon: '💬', border: 'border-blue-200 dark:border-blue-800' },
  nouveaute: { bg: 'from-emerald-500 to-emerald-600', icon: '🆕', border: 'border-emerald-200 dark:border-emerald-800' },
  promotion: { bg: 'from-amber-500 to-amber-600', icon: '🔥', border: 'border-amber-200 dark:border-amber-800' },
  mise_a_jour: { bg: 'from-violet-500 to-violet-600', icon: '⬆️', border: 'border-violet-200 dark:border-violet-800' },
};

const SaasCommsWidget = ({ comms }) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(new Set());
  const visible = comms.filter(c => !dismissed.has(c.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="saas-comms-widget">
      {visible.map(c => {
        const style = COMM_TYPE_STYLES[c.comm_type] || COMM_TYPE_STYLES.info;
        return (
          <Card key={c.id} className={`rounded-2xl overflow-hidden border ${style.border} shadow-sm`} data-testid={`saas-comm-${c.id}`}>
            <div className="flex items-stretch">
              <div className={`w-1.5 bg-gradient-to-b ${style.bg} shrink-0`} />
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Megaphone className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                      <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Vortekio</span>
                    </div>
                    <h3 className="font-bold text-slate-900 dark:text-white text-sm">{c.title}</h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{c.content}</p>
                    {c.link_url && (
                      <a href={c.link_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800 font-semibold">
                        <ExternalLink className="w-3 h-3" />{c.link_label || t('common.learn_more')}
                      </a>
                    )}
                  </div>
                  {c.image_url && (
                    <img src={c.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
                  )}
                  <button onClick={() => setDismissed(prev => new Set(prev).add(c.id))}
                    className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 shrink-0">
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default ReceptionDashboard;
